export const contractors = {
  seo: {
    title: "Bespire for contractors and landscapers — AI assistant for tradespeople",
    description:
      "AI assistant for construction and gardening businesses: your own website with project gallery, online site‑visit booking, digital quotes, social media and Google ads — all in one place.",
  },
  hero: {
    badge: "Construction and gardening",
    title: "Get your own AI assistant — for contractors and landscapers",
    subtitle:
      "A full AI assistant for construction and gardening businesses: it helps you run the daily to‑dos, marketing, quotes, clients and bookings — in one system.",
    ctaSecondary: "Free trial",
    imageAlt: "Construction contractor with hard hat on the job site",
    stats: [
      { value: "10 min", label: "and you have usable business tools" },
      { value: "3 days", label: "free trial" },
    ],
  },
  whatIs: {
    heading: "What is Bespire?",
    description:
      "An AI assistant that helps you run the work around your business: marketing, clients, quotes, bookings and daily to‑dos — so you can focus on the trade and the site.",
    tiles: [
      {
        title: "AI assistant for your daily work",
        description:
          "Just ask it in a chat for a post, ad copy, quote wording or a daily to‑do list — included in your plan, with no separate AI fee.",
      },
      {
        title: "Automatic marketing",
        description:
          "One photo of a finished job and the AI writes the Facebook, Instagram, TikTok post and the Google ad copy for you.",
      },
      {
        title: "Website, booking and clients in one place",
        description:
          "Your own website with a project gallery, online site‑visit booking, digital quotes and client records — usable tools in 10 minutes.",
      },
    ],
  },
  dayInLife: {
    eyebrow: "A day with Bespire",
    heading: "What a typical day looks like for a contractor",
    subheading:
      "Not theory — concrete steps the AI assistant takes off your plate so you can focus on the craft.",
    steps: [
      {
        time: "07:12",
        title: "New site‑visit request from the website",
        description:
          "While you have breakfast, a client books a site visit for next Tuesday — they could only pick from your free slots.",
      },
      {
        time: "09:40",
        title: "Photo on site → post on 3 platforms",
        description:
          "You snap the roof project you finished yesterday. AI generates the Facebook, Instagram and TikTok post with hashtags.",
      },
      {
        time: "12:15",
        title: "Digital quote from a template",
        description:
          "At lunch you send a quote from mobile for the morning site visit. The client opens it and accepts online.",
      },
      {
        time: "15:30",
        title: "AI writes next week's Google ad",
        description:
          "'Write an ad for fence building, late May campaign' — AI gives 3 versions, you pick the best.",
      },
      {
        time: "18:00",
        title: "End of day — zero paperwork",
        description:
          "3 new clients in the CRM, 2 accepted quotes, tomorrow's calendar full. You didn't open a single spreadsheet.",
      },
    ],
  },
  scenarios: {
    heading: "Sound familiar?",
    subheading:
      "3 concrete scenarios for what Bespire takes off your plate — depending on what you do.",
    items: [
      {
        role: "Landscaping business",
        title: "You finished a beautiful garden — but nobody sees it",
        description:
          "Your best work sits on your phone. Bespire drops it into a reference gallery and writes a social post for every finished project.",
        bullets: [
          "Before/after gallery on your website",
          "1 photo → posts for 3 platforms",
          "Seasonal campaigns (spring, autumn) from templates",
        ],
      },
      {
        role: "Construction contractor",
        title: "15 phone calls a day just to schedule",
        description:
          "Clients book site visits themselves on your website — they can only pick free slots. Then AI writes the quote from your notes.",
        bullets: [
          "Online site‑visit booking tied to your calendar",
          "Quote templates — done in 2 minutes",
          "Card deposit optionally",
        ],
      },
      {
        role: "You lead a team",
        title: "Subcontractors, deadlines, chaos",
        description:
          "You can add multiple users with permissions. Everyone sees their own day, you track everything from one place. For intensive daily use the Premium plan is the optimised choice.",
        bullets: [
          "Separate login for team members",
          "Project tracking with status",
          "Automatic deadline reminders",
        ],
      },
    ],
  },
  inAction: {
    eyebrow: "In practice",
    heading: "This is what your client sees when they meet you",
    subheading:
      "Three concrete moments the client experiences — a professional impression, no IT skills required.",
    screens: [
      {
        title: "Your website on the client's phone",
        description:
          "Gallery, services, 'Request a site visit' button — Google finds you too.",
      },
      {
        title: "AI chat: 'Write a post about today's job'",
        description:
          "Tell it in a chat what you want. AI writes the post, you just approve it.",
      },
      {
        title: "Digital quote, accept in one click",
        description:
          "The client opens it on their phone, reads it and accepts online — you see when they viewed it.",
      },
    ],
  },
  parallax: {
    alt: "Swimming pool installation",
  },
  packages: {
    eyebrow: "Plans",
    heading: "Pick the plan that fits your business",
    subheading:
      "Monthly or annual subscription — net prices, + VAT. Switch or cancel any time.",
    trialNote: "3‑day free trial · No card required · Cancel any time",
    recommended: "Recommended",
    ctaPrimary: "Try it free",
    ctaSecondary: "Request callback",
    perMonth: "/ mo",
    perMonthShort: "/ mo",
    perYearShort: "/ yr",
    savingLabel: "Annual saving",
    billingToggle: {
      monthly: "Monthly",
      annual: "Annual",
      saveBadge: "2 months free",
      annualHint: "With annual billing we waive 2 months — the annual fee is the monthly price × 10.",
    },
    items: [
      {
        name: "Growth",
        credits: "Approx. 5–10 AI tasks per day",
        features: [
          "The full system: website, booking, payments",
          "AI for your daily work — included in the plan",
          "Digital quotes and online payments",
          "Automatic marketing tools",
        ],
        monthly: {
          price: "HUF 12,900 + VAT",
        },
        annual: {
          price: "HUF 10,750 + VAT",
          perYearLabel: "Annual fee: HUF 129,000 + VAT",
          saving: "HUF 25,800 + VAT",
        },
      },
      {
        name: "Premium",
        credits: "20+ AI tasks per day",
        features: [
          "Everything in Growth — optimised for more intensive use",
          "Higher load capacity during campaigns — up to 20+ AI tasks a day",
          "Priority support — faster response times for intensive operations",
        ],
        monthly: {
          price: "HUF 24,900 + VAT",
        },
        annual: {
          price: "HUF 20,750 + VAT",
          perYearLabel: "Annual fee: HUF 249,000 + VAT",
          saving: "HUF 49,800 + VAT",
        },
      },
    ],
  },
  benefits: {
    heading: "Why it pays off as a contractor or landscaper",
    items: [
      {
        title: "Your own website with a project gallery",
        description:
          "Before/after photos, projects, client reviews — future customers see exactly what you can do. Google‑optimised too.",
      },
      {
        title: "Online site‑visit booking",
        description:
          "Clients request an on‑site visit right on your website — they can only pick free slots, no back‑and‑forth calls.",
      },
      {
        title: "Digital quotes",
        description:
          "Send quotes from templates in minutes, see when they were opened, and let clients accept them online.",
      },
      {
        title: "Client CRM and follow‑ups",
        description:
          "Every lead, site visit and project in one place — automatic reminders for warranty check‑ins and annual maintenance.",
      },
      {
        title: "Marketing and social media",
        description:
          "AI generates Facebook, Instagram, TikTok posts and Google ad copy for you — even from a single project photo.",
      },
      {
        title: "GDPR + EU server",
        description:
          "Your data is stored on EU servers, never used for AI training, and never sold.",
      },
    ],
  },
  replaces: {
    heading: "What does Bespire replace?",
    description:
      "Instead of a separate website, booking system, social media manager and marketing agency — one AI assistant.",
    leftLabel: "Without separate tools",
    rightLabel: "With Bespire",
    unified: "One system — with every feature together",
    aiBonus: "AI assistant — helps you become a more successful business",
    rightItems: [
      "Website + online site‑visit booking",
      "Digital quotes and online payments",
      "Facebook, Instagram, TikTok posts",
      "Google ad copy and images",
      "Client and project records",
    ],
    items: [
      "Separate web developer and domain management",
      "Separate online booking / calendar system",
      "Separate social media manager",
      "Separate marketing agency for ads",
      "Excel sheets and paper quotes",
    ],
  },
  continuousSupport: {
    eyebrow: "Support",
    heading: "You're not alone with the AI assistant",
    description:
      "We regularly host workshops for contractors and landscapers where we help you use the AI assistant, answer your questions, and let business owners share ideas with each other.",
    items: [
      {
        title: "Dedicated business owner workshops",
        description:
          "Regular events for contractors and landscapers — where we explore every feature of the AI assistant together.",
      },
      {
        title: "Live Q&A",
        description:
          "We answer questions on the spot — whether about the website, quotes or ads.",
      },
      {
        title: "Practical assistant use",
        description:
          "Concrete tips for seasonal campaigns, quotes and client acquisition — practice, not theory.",
      },
      {
        title: "Community idea sharing",
        description:
          "Peer exchange between business owners — how you use Bespire in your own trade.",
      },
    ],
  },
  howToStart: {
    heading: "How do you start?",
    subheading: "Choose one of two paths — both give you a 3‑day free trial.",
    paths: [
      {
        badge: "On your own",
        title: "Start free with 3 days of full access",
        duration: "≈ 10 min",
        description:
          "You sign up and immediately get 3 days of full access to everything — set up the AI assistant yourself, or join an online workshop.",
        steps: [
          "Sign up on Bespire",
          "Try everything for 3 days — no card required",
          "Upload your reference photos and services",
          "Publish your website — take the first site visit",
        ],
        cta: "Try it free",
        ctaHref: "/onboarding",
      },
      {
        badge: "With us",
        title: "We'll set it up for you",
        duration: "≈ 1 business day",
        description:
          "We call you back, agree on the details and set up your AI assistant — for a one‑off HUF 50,000 + VAT setup fee. You just start using it.",
        steps: [
          "Request a callback",
          "We discuss your needs on the phone",
          "We set up the website, booking and templates",
          "We hand it over turn‑key — send the first quote",
        ],
        cta: "Request callback",
        ctaHref: "/smb-pricing#callback",
      },
    ],
  },
  faq: {
    heading: "Frequently asked questions from business owners",
    items: [
      {
        q: "Do I need any IT skills?",
        a: "No. The AI assistant helps you set everything up in a chat — you have usable business tools in 10 minutes, and we work from templates.",
      },
      {
        q: "Can I use my existing domain?",
        a: "Yes. You can use your own domain, or you'll get a free subdomain within Bespire.",
      },
      {
        q: "What happens to my reference photos?",
        a: "Your images are stored on EU servers, never used for AI training, and never shared. You can download or delete them any time.",
      },
      {
        q: "Is it also good for teams and subcontractors?",
        a: "Yes. You can add multiple users with permissions — your field colleagues can see their own day too. For intensive daily use the Premium plan is optimised.",
      },
      {
        q: "Is there a cancellation clause?",
        a: "You can cancel any time, there is no long‑term commitment — it stays active until the next billing cycle.",
      },
      {
        q: "How long is the trial?",
        a: "You get 3 days of free full access to the AI assistant and every feature. No card required, cancel any time.",
      },
    ],
  },
  finalCta: {
    heading: "Start for free",
    description:
      "Launch Bespire with 3 days of free full access — no card required, cancel any time.",
    ctaPrimary: "Free start",
    ctaSecondary: "Let's talk on the phone",
  },
};
